import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { ModalPopup } from "../../common";
import { AnalyticsSavedFilterVisibility } from "../../../types/analytics/AnalyticsSavedFilter";

interface AnalyticsSaveFilterDialogProps {
  isOpen: boolean;
  onOpenChange(open: boolean): void;
  /** True when the save includes journey choices (page-bound, not portable). */
  isPageBound: boolean;
  pageTitle: string;
  /** Readable Show past data date this save will store, or null when live. */
  pointInTimeDateLabel?: string | null;
  /**
   * ADR-0006: warn (do not block) when Show past data and user groups are both set.
   */
  showUserGroupsPastDataWarning?: boolean;
  /** Prefill name when saving a copy of an applied filter. */
  initialName?: string;
  /** When true, skip Update choice and open the create form. */
  forceCreateOnly?: boolean;
  /** Offer Update when an editable saved filter is still applied. */
  canUpdate?: boolean;
  updateTargetName?: string | null;
  canManageShared?: boolean;
  nameError?: string | null;
  onClearNameError?(): void;
  onSave(args: {
    name: string;
    visibility: AnalyticsSavedFilterVisibility;
  }): void;
  onUpdate?(): void;
  isSaving?: boolean;
}

function PastDataSaveNotices({
  pointInTimeDateLabel,
  showUserGroupsPastDataWarning,
  t,
}: {
  pointInTimeDateLabel: string | null;
  showUserGroupsPastDataWarning: boolean;
  t: (key: string, defaultValueOrOptions?: string | object, options?: object) => string;
}) {
  if (!pointInTimeDateLabel && !showUserGroupsPastDataWarning) {
    return null;
  }

  return (
    <>
      {pointInTimeDateLabel && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-gray-700">
          {t(
            "Analytics.Filters.SaveDialog.PastDataNote",
            "A data snapshot is on. This filter will open as at {{date}} until you change it.",
            { date: pointInTimeDateLabel },
          )}
        </div>
      )}
      {showUserGroupsPastDataWarning && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-gray-700">
          {t(
            "Analytics.Filters.SaveDialog.PastDataUserGroupsWarning",
            "User groups stay current even with a data snapshot. Prefer a group named for this period, and create a new group later, instead of editing the same living group.",
          )}
        </div>
      )}
    </>
  );
}

function AnalyticsSaveFilterDialog({
  isOpen,
  onOpenChange,
  isPageBound,
  pageTitle,
  pointInTimeDateLabel = null,
  showUserGroupsPastDataWarning = false,
  initialName = "",
  forceCreateOnly = false,
  canUpdate = false,
  updateTargetName = null,
  canManageShared = false,
  nameError = null,
  onClearNameError,
  onSave,
  onUpdate,
  isSaving = false,
}: AnalyticsSaveFilterDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [visibility, setVisibility] =
    useState<AnalyticsSavedFilterVisibility>("PERSONAL");
  const [showCreateForm, setShowCreateForm] = useState(true);

  useEffect(() => {
    if (!isOpen) return;
    const suggestedName = initialName.trim()
      ? `${initialName.trim()} (copy)`.slice(0, 100)
      : "";
    setName(suggestedName);
    setVisibility("PERSONAL");
    setShowCreateForm(forceCreateOnly || !canUpdate);
  }, [isOpen, initialName, forceCreateOnly, canUpdate]);

  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
  };

  const pastDataNotices = (
    <PastDataSaveNotices
      pointInTimeDateLabel={pointInTimeDateLabel}
      showUserGroupsPastDataWarning={showUserGroupsPastDataWarning}
      t={t}
    />
  );

  if (isOpen && canUpdate && !showCreateForm) {
    return (
      <ModalPopup
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        title={t("Analytics.Filters.SaveDialog.Title", "Save filter")}
        width="TINY"
        secondaryButtonText={t("Common.Cancel", "Cancel")}
        isLoading={isSaving}
        onSecondaryButtonClick={() => onOpenChange(false)}
      >
        <div className="space-y-2">
          {pastDataNotices}
          <button
            type="button"
            className="w-full rounded-md border border-gray-200 px-3 py-3 text-left hover:bg-gray-50 disabled:opacity-50"
            disabled={isSaving}
            onClick={() => onUpdate?.()}
          >
            <div className="text-sm font-semibold text-gray-800">
              {t(
                "Analytics.Filters.SaveDialog.UpdateNamed",
                "Update '{{name}}'",
                { name: updateTargetName ?? "" },
              )}
            </div>
            <div className="mt-0.5 text-xs text-gray-500">
              {t(
                "Analytics.Filters.SaveDialog.UpdateNamedHint",
                "Replace it with your current filters",
              )}
            </div>
          </button>
          <button
            type="button"
            className="w-full rounded-md border border-gray-200 px-3 py-3 text-left hover:bg-gray-50 disabled:opacity-50"
            disabled={isSaving}
            onClick={() => setShowCreateForm(true)}
          >
            <div className="text-sm font-semibold text-gray-800">
              {t(
                "Analytics.Filters.SaveDialog.SaveAsNew",
                "Save as a new filter",
              )}
            </div>
            <div className="mt-0.5 text-xs text-gray-500">
              {t(
                "Analytics.Filters.SaveDialog.SaveAsNewHint",
                "Keep the existing one and create another",
              )}
            </div>
          </button>
        </div>
      </ModalPopup>
    );
  }

  return (
    <ModalPopup
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      title={t("Analytics.Filters.SaveDialog.Title", "Save filter")}
      titleDescription={t(
        "Analytics.Filters.SaveDialog.Subtitle",
        "Saves this filter combination so that it's easy to re-use in the future",
      )}
      width="SMALL"
      primaryButtonText={t("Analytics.Filters.SaveDialog.Save", "Save filter")}
      secondaryButtonText={t("Common.Cancel", "Cancel")}
      isLoading={isSaving}
      isPrimaryButtonDisabled={!name.trim() || isSaving}
      onPrimaryButtonClick={() => {
        if (!name.trim()) return;
        onSave({ name: name.trim(), visibility });
      }}
      onSecondaryButtonClick={() => onOpenChange(false)}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            {t("Analytics.Filters.SaveDialog.Name", "Name")}
          </label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            value={name}
            maxLength={100}
            onChange={(e) => {
              setName(e.target.value);
              onClearNameError?.();
            }}
            autoFocus
          />
          {nameError && (
            <p className="mt-1.5 text-xs text-red-600">{nameError}</p>
          )}
        </div>

        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 flex items-start gap-2">
          <FontAwesomeIcon
            icon={faInfoCircle}
            className="mt-0.5 shrink-0 text-gray-500"
            aria-hidden
          />
          <span>
            {isPageBound
              ? t(
                  "Analytics.Filters.SaveDialog.JourneyScope",
                  "Includes journey choices. On other pages, only the journeys available there will apply.",
                )
              : t(
                  "Analytics.Filters.SaveDialog.PortableScope",
                  "This one will work on any analytics page, because it only filters by audience.",
                )}
          </span>
        </div>

        {canManageShared && (
          <div>
            <div className="text-sm font-semibold text-gray-700 mb-2">
              {t("Analytics.Filters.SaveDialog.WhoCanUse", "Who can use this")}
            </div>
            <div className="space-y-2 px-1 py-0.5">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  className="mt-1"
                  checked={visibility === "PERSONAL"}
                  onChange={() => setVisibility("PERSONAL")}
                />
                <span>
                  <span className="font-semibold text-sm text-gray-800">
                    {t("Analytics.Filters.SaveDialog.JustMe", "Just me")}
                  </span>
                  <br />
                  <span className="text-xs text-gray-500">
                    {t(
                      "Analytics.Filters.SaveDialog.JustMeHint",
                      "Nobody else will see it.",
                    )}
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  className="mt-1"
                  checked={visibility === "SHARED"}
                  onChange={() => setVisibility("SHARED")}
                />
                <span>
                  <span className="font-semibold text-sm text-gray-800">
                    {t("Analytics.Filters.SaveDialog.Everyone", "Everyone")}
                  </span>
                  <br />
                  <span className="text-xs text-gray-500">
                    {t(
                      "Analytics.Filters.SaveDialog.EveryoneHint",
                      "Anyone can use it. Only people who manage shared filters can change or delete it.",
                    )}
                  </span>
                </span>
              </label>
            </div>
          </div>
        )}

        {pastDataNotices}
      </div>
    </ModalPopup>
  );
}

export default AnalyticsSaveFilterDialog;
